<template>
  <PageTemplate>

    <BackButton
        :label="$t('general.button.back')"
        :to="{ name: 'addressBookPartnerPracticesList' }"
        marginBottom="5"/>

    <div class="d-flex justify-content-between">
      <h6 class="element-header">
        {{ $t('general.contacts') }}
      </h6>
    </div>

    <div class="element-box">
      <h5 class="form-header mb-4">
        {{ $t('address_book.partner_practices') }}
      </h5>

      <form @submit.prevent="submitForm" novalidate>
        <div class="row">
          <div class="col-lg-6 col-md-6 col-sm-12">
            <BaseInput v-model="formData.firstName" :label="$t('general.first_name')"></BaseInput>
            <BaseInput v-model="formData.lastName" :label="$t('general.last_name')"></BaseInput>
            <BaseInput v-model="formData.company" :label="$t('general.company_client')"></BaseInput>
            <BaseInput v-model="formData.phoneNumber" :label="$t('general.phone_number')"></BaseInput>
            <BaseInput
                v-model="formData.email"
                :validation="v$.email"
                :messages="customMessages.email.invalid"
                :label="$t('general.email')" type="email">
            </BaseInput>
          </div>
          <div class="col-lg-6 col-md-6 col-sm-12">
            <BaseInput v-model="formData.address" :label="$t('general.street')"></BaseInput>
            <BaseInput v-model="formData.zip" :label="$t('general.postal_code')"></BaseInput>
            <BaseInput v-model="formData.city" :label="$t('general.city')"></BaseInput>
            <BaseInput v-model="formData.position" :label="$t('general.position')"></BaseInput>
            <SelectVue
                v-model="formData.federalState"
                :options="federalStatesDropdown"
                :required="false"
                :is-clear="true"
                :label="$t('general.federal_state')"
                :placeholder="$t('placeholder.select_federal_state')"/>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div>
              <TextEditor
                  v-model="formData.info"
                  @update:model-value="formData.info = $event"
                  :inputStyle="{ height: '200px'}"
                  :label="$t('general.info')"/>
            </div>
          </div>
        </div>
        <div class="form-buttons-w text-center">
          <SaveButton
              :label="$t('general.button.save')"
              :loading="isSubmitting"/>
        </div>
      </form>
    </div>
  </PageTemplate>
</template>

<script setup lang="ts">
import partnerPracticesService from '@/api/AddressBook/partnerPractices';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import SelectVue from '@/components/SelectVue.vue';
import TextEditor from '@/components/TextEditor.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import useVuelidate from '@vuelidate/core';
import {email as emailValidator, helpers} from '@vuelidate/validators';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {IAddressBook} from "@/interfaces/AddressBook/IAddressBook";
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const federalStatesDropdown = ref([]);
const route = useRoute();
const partnerId = route.params.id;
const isSubmitting = ref(false);

const formData = ref<IAddressBook>({
  firstName: '',
  lastName: '',
  company: '',
  phoneNumber: '',
  email: '',
  address: '',
  zip: '',
  city: '',
  position: '',
  federalState: null,
  info: '',
});

const customMessages = {
  //federalState: {
  //  required: t('form_validation.required_field'),
  //},
  email: {
    invalid: t('form_validation.invalid_email'),
  },
};

const rules = computed(() => {
  return {
    email: {valid: helpers.withMessage(customMessages.email.invalid, emailValidator)},
    //federalState: {
    //  required: helpers.withMessage(customMessages.federalState.required,
    //      (value) => value !== '' && value !== null,
    //  ),
    //},
  };
});

const v$ = useVuelidate(rules, formData);

const submitForm = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  if (formData.value.federalState === '') {
    formData.value.federalState = null
  }

  try {
    partnerId ?
        await partnerPracticesService.putPartnerPractice(formData.value, +partnerId) :
        await partnerPracticesService.postPartnerPractice(formData.value)

    await router.push({name: 'addressBookPartnerPracticesList'});
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }

};

async function getPartnerPracticeData() {
  try {
    const {data} = await partnerPracticesService.getSinglePartnerPractice(+partnerId);
    formData.value = data.data;

    if (formData.value.federalState == null) {
      formData.value.federalState = '';
    }
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getFederalStateDropdown() {
  federalStatesDropdown.value = getEnums.value.federalStates;
}

onMounted(async () => {
  CloseToast()
  await getFederalStateDropdown();

  if (partnerId) {
    await getPartnerPracticeData();
  }
});

</script>

<style scoped>

</style>